/* =============================================================
   pi-swipe — Before / After image comparison
   Ported verbatim (behavior-preserving) from the PublicInput
   Frontend Public Design System handoff for Feature #9271.

   Vanilla JS, no dependencies. Initializes every [data-pi-swipe]
   on the page as an independent instance (no shared IDs or globals
   that would collide).

   Packaged as an ES module: puckBundle.js imports { piSwipeInit }
   and calls it after rendering a Swipe widget. The module also
   exposes window.piSwipeInit and self-initializes on DOMContentLoaded
   so server-rendered markup (or AJAX-inserted markup) works too.

   Per-instance configuration is read from data-* attributes on the
   .pi-swipe element (all optional):
     data-pi-swipe-ratio   "16:9" | "4:3" | "1:1" | "3:2" | "16/9"
     data-pi-swipe-start   starting divider position, 0-100 (default 25)
     data-pi-before-focus  "x y" focal point % for the before image
     data-pi-after-focus   "x y" focal point % for the after image
   ============================================================= */

function clamp(n, lo, hi) {
  return Math.max(lo, Math.min(hi, n));
}

function applyFocus(root, attr, propX, propY) {
  var v = root.getAttribute(attr);
  if (!v) return;
  var parts = v.trim().split(/[\s,]+/);
  if (parts.length !== 2) return;
  root.style.setProperty(propX, parseFloat(parts[0]) + "%");
  root.style.setProperty(propY, parseFloat(parts[1]) + "%");
}

function initSwipe(root) {
  if (root.__piSwipeReady) return; // guard against double-init
  var stage = root.querySelector(".pi-swipe__stage");
  var range = root.querySelector(".pi-swipe__range");
  if (!stage || !range) return; // markup incomplete — leave stacked fallback
  root.__piSwipeReady = true;

  // Frame shape (also settable via a modifier class in CSS).
  var ratio = root.getAttribute("data-pi-swipe-ratio");
  if (ratio) {
    var r = ratio.split(/[:/x]/);
    if (r.length === 2) {
      // Coerce both sides to numbers before writing into a style property
      // (defense-in-depth, mirroring applyFocus's parseFloat guard).
      var rw = parseFloat(r[0]);
      var rh = parseFloat(r[1]);
      if (!isNaN(rw) && !isNaN(rh) && rh !== 0) {
        stage.style.aspectRatio = rw + " / " + rh;
      }
    }
  }

  // Focal points for mismatched images.
  applyFocus(
    root,
    "data-pi-before-focus",
    "--pi-swipe-before-x",
    "--pi-swipe-before-y"
  );
  applyFocus(
    root,
    "data-pi-after-focus",
    "--pi-swipe-after-x",
    "--pi-swipe-after-y"
  );

  function setPos(v) {
    v = clamp(v, 0, 100);
    root.style.setProperty("--pi-swipe-pos", v);
    var shown = Math.round(v);
    if (String(range.value) !== String(v)) range.value = v;
    // Native range provides aria-valuenow (= value). We author the text.
    range.setAttribute("aria-valuetext", shown + "% of the after image shown");
  }

  range.addEventListener("input", function () {
    setPos(parseFloat(range.value));
  });

  // Reveal enhanced UI and set the starting position.
  root.classList.add("pi-swipe--ready");
  var start = parseFloat(root.getAttribute("data-pi-swipe-start"));
  if (isNaN(start)) start = parseFloat(range.value);
  if (isNaN(start)) start = 25;
  setPos(start);
}

/**
 * Initialize every [data-pi-swipe] within an optional scope (defaults to document).
 * Safe to call repeatedly — already-initialized instances are skipped.
 * @param {Element|Document} [context]
 */
export function piSwipeInit(context) {
  var scope = context || document;
  var list = scope.querySelectorAll("[data-pi-swipe]");
  for (var i = 0; i < list.length; i++) initSwipe(list[i]);
}

// Expose a global hook for server-rendered / AJAX-inserted markup.
if (typeof window !== "undefined") {
  window.piSwipeInit = piSwipeInit;
}

// Self-initialize static markup that is present at load time.
if (typeof document !== "undefined") {
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () {
      piSwipeInit();
    });
  } else {
    piSwipeInit();
  }
}
